import { useEffect, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { CircleNotch, Copy, Sparkle, ArrowsClockwise, Trash, Megaphone, CheckCircle, FilmSlate, DownloadSimple, CopySimple, CalendarPlus, Clock, Eraser, ListBullets, CalendarBlank, ShieldCheck, CaretLeft, CaretRight, CaretDown, CaretUp, Plus, Image as ImageIcon, UploadSimple, PaperPlaneTilt, ChartLineUp, LinkSimple, Gear } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const THEME_LABELS = {
  pain_points: "Pain points",
  benefits: "Product benefits",
  tips: "Quick tips",
  myth_busting: "Myth-busting",
  awareness: "Awareness",
};
// Order the post sections are displayed in (List view).
const SECTION_ORDER = ["awareness", "pain_points", "benefits", "tips", "myth_busting"];
const PLATFORMS = [["linkedin", "LinkedIn"], ["x", "X (Twitter)"], ["instagram", "Instagram"]];
const TONES = [["formal", "Formal"], ["balanced", "Balanced"], ["playful", "Playful"]];
const THEME_KEYWORDS = {
  pain_points: ["prepay", "accru", "month", "defer"],
  benefits: ["prepay", "accru", "defer", "journal"],
  tips: ["prepay", "accru", "defer", "reconc", "close"],
  myth_busting: ["prepay", "accru", "spreadsheet", "defer"],
  awareness: ["connect", "xero", "getting started", "prepay"],
};
const BEST_TIMES = {
  linkedin: "Best Tue–Thu, 9–11am",
  x: "Best weekdays, 12–1pm & 5–6pm",
  instagram: "Best Mon/Wed/Fri, 11am–1pm",
};
const THEME_DOT = {
  pain_points: "#EF4444",
  benefits: "#16A34A",
  tips: "#2563EB",
  myth_busting: "#9333EA",
  awareness: "#C2410C",
};
const WEEKDAY_HDRS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BG_STYLES = [
  { key: "lottie", label: "Lottie", css: { backgroundImage: "url('/lottie-features.jpg')", backgroundSize: "cover", backgroundPosition: "center" } },
  { key: "navy", label: "Navy", css: { background: "#0B1A2F" } },
  { key: "gradient", label: "Gradient", css: { background: "linear-gradient(#162E4E,#071121)" } },
  { key: "dots", label: "Dots", css: { background: "#0B1A2F", backgroundImage: "radial-gradient(#22385c 1.5px, transparent 1.5px)", backgroundSize: "6px 6px" } },
  { key: "frame", label: "Frame", css: { background: "#0B1A2F", boxShadow: "inset 0 0 0 3px #F37A24" } },
  { key: "diagonal", label: "Diagonal", css: { background: "linear-gradient(135deg,#F37A24 0 26%,#0B1A2F 26%)" } },
];

const combine = (v) =>
  v ? [v.text, (v.hashtags || []).map((h) => `#${h}`).join(" ")].filter(Boolean).join("\n\n") : "";

const trackedLink = (id, channel) => `https://workwithlottie.com/start?ref=wp_${id}${channel ? `&ch=${channel}` : ""}`;
const LINK_CHANNELS = [["linkedin", "LinkedIn"], ["facebook", "Facebook"], ["instagram", "Instagram"]];

export default function SocialCampaign() {
  const [posts, setPosts] = useState([]);
  const [themes, setThemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [tone, setTone] = useState("balanced");
  const [edits, setEdits] = useState({});
  const [activePlat, setActivePlat] = useState({});
  const [regenId, setRegenId] = useState(null);
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [clipDl, setClipDl] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [overwrite, setOverwrite] = useState(false);
  const [autoFilling, setAutoFilling] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [bulkImg, setBulkImg] = useState(false);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("campaign_default_view") || "list");
  const [dragId, setDragId] = useState(null);
  const [dragOverDay, setDragOverDay] = useState(null);
  const [focusId, setFocusId] = useState(null);
  const [imgBusy, setImgBusy] = useState(null);
  const [pickerDay, setPickerDay] = useState(null);
  const [pickerPlat, setPickerPlat] = useState("linkedin");
  const [collapsed, setCollapsed] = useState({});
  const [defaultView, setDefaultView] = useState(() => localStorage.getItem("campaign_default_view") || "list");
  const [collapseDefault, setCollapseDefault] = useState(() => localStorage.getItem("campaign_collapse_default") === "true");
  const [showViewPrefs, setShowViewPrefs] = useState(false);
  const [campaignOnly, setCampaignOnly] = useState(false);
  const [bulkCopy, setBulkCopy] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [linkedinShare, setLinkedinShare] = useState(null);
  const [imgCopyBusy, setImgCopyBusy] = useState(false);

  const seedFrom = (list) => {
    const e = {}, p = {};
    list.forEach((post) => {
      e[post.id] = {
        linkedin: combine(post.platforms.linkedin),
        x: combine(post.platforms.x),
        instagram: combine(post.platforms.instagram),
      };
      p[post.id] = "linkedin";
    });
    return { e, p };
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/learning/campaign/posts");
      setPosts(data.posts || []);
      setThemes(data.themes || []);
      const { e, p } = seedFrom(data.posts || []);
      setEdits(e); setActivePlat(p);
    } catch { toast.error("Could not load posts"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadAnalytics(); }, []); // eslint-disable-line

  useEffect(() => {
    api.get("/admin/learning/campaign/walkthroughs")
      .then(({ data }) => setWalkthroughs(data.walkthroughs || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!focusId || viewMode !== "list") return;
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-testid="campaign-post-${focusId}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-orange-400");
    });
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="campaign-post-${focusId}"]`);
      if (el) el.classList.remove("ring-2", "ring-orange-400");
      setFocusId(null);
    }, 2400);
    return () => { cancelAnimationFrame(raf); clearTimeout(t); };
  }, [focusId, viewMode, posts, collapsed]);

  const jumpToPost = (id) => {
    const p = posts.find((x) => x.id === id);
    if (p) setCollapsed((s) => ({ ...s, [p.theme]: false })); // ensure its group is expanded
    setViewMode("list");
    setFocusId(id);
  };

  const mediaUrl = (post) => post.image_path ? `${API}/learning/campaign/media/${post.id}?v=${encodeURIComponent(post.image_updated || "")}` : null;

  const genImage = async (id, aspect = "1:1", bg = "lottie") => {
    setImgBusy(id);
    try {
      const { data } = await api.post(`/admin/learning/campaign/posts/${id}/image/generate`, { aspect, bg });
      setPosts((list) => list.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Branded image created");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create image"); }
    finally { setImgBusy(null); }
  };

  const uploadImage = async (id, file) => {
    if (!file) return;
    setImgBusy(id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/admin/learning/campaign/posts/${id}/image/upload`, fd);
      setPosts((list) => list.map((x) => (x.id === id ? { ...x, ...data } : x)));
      toast.success("Image uploaded");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not upload image"); }
    finally { setImgBusy(null); }
  };

  const removeImage = async (id) => {
    try {
      await api.delete(`/admin/learning/campaign/posts/${id}/image`);
      setPosts((list) => list.map((x) => (x.id === id
        ? { ...x, image_path: null, image_source: null, image_aspect: null, image_updated: null } : x)));
    } catch { toast.error("Could not remove image"); }
  };

  const captionText = (post, plat) => {
    const v = post.platforms?.[plat] || {};
    return [v.text, (v.hashtags || []).map((h) => `#${h}`).join(" ")].filter(Boolean).join("\n\n");
  };

  const publishAssist = async (post, plat) => {
    const caption = `${captionText(post, plat)}\n\n${trackedLink(post.id, plat)}`;
    try { await navigator.clipboard.writeText(caption); } catch { /* clipboard may be blocked */ }
    if (post.image_path) {
      const a = document.createElement("a");
      a.href = mediaUrl(post); a.download = `${post.id}.png`; a.target = "_blank";
      document.body.appendChild(a); a.click(); a.remove();
    }
    const composer = {
      x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}`,
      linkedin: "https://www.linkedin.com/feed/?shareActive=true",
      instagram: "https://www.instagram.com/",
    }[plat];
    window.open(composer, "_blank", "noopener,noreferrer");
    toast.success(plat === "x"
      ? "Opening X with your caption pre-filled" + (post.image_path ? " — image downloaded to attach" : "")
      : `Caption copied${post.image_path ? " + image downloaded" : ""} — paste it into ${plat === "linkedin" ? "LinkedIn" : "Instagram"}`);
  };

  // LinkedIn won't accept text/images via a share URL, so we give a guided copy flow.
  const linkedinCaption = (post) => `${captionText(post, "linkedin")}\n\n${trackedLink(post.id, "linkedin")}`;

  const copyLinkedinText = async (post) => {
    try {
      await navigator.clipboard.writeText(linkedinCaption(post));
      toast.success("Caption + link copied — paste it into your LinkedIn post");
    } catch { toast.error("Couldn't copy the text"); }
  };

  const copyImageToClipboard = async (post) => {
    if (!post.image_path) return;
    setImgCopyBusy(true);
    try {
      const res = await fetch(mediaUrl(post), { credentials: "include" });
      const blob = await res.blob();
      let pngBlob = blob;
      if (blob.type !== "image/png") {
        pngBlob = await new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            const c = document.createElement("canvas");
            c.width = img.naturalWidth; c.height = img.naturalHeight;
            c.getContext("2d").drawImage(img, 0, 0);
            c.toBlob((b) => (b ? resolve(b) : reject(new Error("convert"))), "image/png");
          };
          img.onerror = reject;
          img.src = URL.createObjectURL(blob);
        });
      }
      await navigator.clipboard.write([new window.ClipboardItem({ "image/png": pngBlob })]);
      toast.success("Image copied — paste it straight into the LinkedIn composer");
    } catch {
      toast.error("Couldn't copy the image here — use Download instead, then attach it");
    } finally { setImgCopyBusy(false); }
  };

  const downloadImage = (post) => {
    if (!post.image_path) return;
    const a = document.createElement("a");
    a.href = mediaUrl(post); a.download = `${post.id}.png`; a.target = "_blank";
    document.body.appendChild(a); a.click(); a.remove();
  };

  const shiftMonth = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const monthLabel = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  };
  const assignToDay = (id) => {
    if (pickerDay) { patchPost(id, { scheduled_day: pickerDay }); toast.success(`Scheduled for ${pickerDay}`); }
    setPickerDay(null);
  };
  const unscheduledPosts = posts.filter((p) => !p.scheduled_day && !p.posted);

  const suggestFor = (theme) => {
    const kws = THEME_KEYWORDS[theme] || [];
    const hit = walkthroughs.find((w) => kws.some((k) => (w.title || "").toLowerCase().includes(k)));
    return (hit || walkthroughs[0] || {}).slug || "";
  };

  const patchPost = async (id, patch) => {
    let prev;
    setPosts((list) => list.map((x) => {
      if (x.id === id) { prev = x; return { ...x, ...patch }; }
      return x;
    }));
    try { await api.patch(`/admin/learning/campaign/posts/${id}`, patch); }
    catch {
      toast.error("Could not save");
      if (prev) setPosts((list) => list.map((x) => (x.id === id ? prev : x)));
    }
  };

  const downloadShort = async (id, slug) => {
    if (!slug) { toast.error("Pick a video short first"); return; }
    setClipDl(id);
    try {
      const res = await api.get(`/admin/learning/social/${slug}/clip`, { params: { aspect: "9:16" }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${slug}-vertical-clip.mp4`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not build clip"); }
    finally { setClipDl(null); }
  };

  const autoFill = async () => {
    setAutoFilling(true);
    try {
      const { data } = await api.post("/admin/learning/campaign/auto-fill", { month, overwrite });
      toast.success(`Scheduled ${data.count} post${data.count === 1 ? "" : "s"} across ${month}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not auto-fill the month"); }
    finally { setAutoFilling(false); }
  };

  const bulkImages = async () => {
    setBulkImg(true);
    try {
      const { data } = await api.post("/admin/learning/campaign/images/bulk-generate", { month, overwrite });
      toast.success(`Created ${data.count} image${data.count === 1 ? "" : "s"} for ${monthLabel(month)}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not generate images"); }
    finally { setBulkImg(false); }
  };

  const bulkCopyMonth = async () => {
    setBulkCopy(true);
    try {
      const { data } = await api.post("/admin/learning/campaign/copy/bulk-generate", { month, tone, overwrite });
      toast.success(`Lottie drafted copy for ${data.count} post${data.count === 1 ? "" : "s"} in ${monthLabel(month)}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not draft copy"); }
    finally { setBulkCopy(false); }
  };

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const { data } = await api.get("/admin/learning/campaign/analytics");
      setAnalytics(data);
    } catch { toast.error("Could not load analytics"); }
    finally { setLoadingAnalytics(false); }
  };

  const toggleAnalytics = () => {
    const next = !showAnalytics;
    setShowAnalytics(next);
    if (next && !analytics) loadAnalytics();
  };

  const copyTracked = async (id, channel) => {
    try { await navigator.clipboard.writeText(trackedLink(id, channel)); toast.success(`${channel ? channel[0].toUpperCase() + channel.slice(1) : "Tracked"} link copied — visits show in Analytics`); }
    catch { toast.error("Could not copy link"); }
  };

  const [seeding, setSeeding] = useState(false);
  const [showSeedDialog, setShowSeedDialog] = useState(false);
  const [seedStart, setSeedStart] = useState("2026-08-03");
  const seedLaunch = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/learning/campaign/seed-launch", { start_date: seedStart });
      toast.success(`Loaded ${data.count} launch posts from ${data.start} — images are generating in the background`, { duration: 6000 });
      setShowSeedDialog(false);
      if (data.start_month) setMonth(data.start_month);
      setTimeout(load, 1500);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not load the launch campaign"); }
    finally { setSeeding(false); }
  };

  const applyDefaultView = (v) => {
    setDefaultView(v);
    localStorage.setItem("campaign_default_view", v);
    setViewMode(v);
    toast.success(`${v === "list" ? "List" : "Calendar"} is now the default view`);
  };

  const applyCollapseDefault = (on) => {
    setCollapseDefault(on);
    localStorage.setItem("campaign_collapse_default", on ? "true" : "false");
    setCollapsed({}); // clear explicit toggles so every group follows the new default
  };

  const clearMonth = async () => {    setClearing(true);
    try {
      const { data } = await api.post("/admin/learning/campaign/clear-month", { month });
      toast.success(data.cleared ? `Cleared ${data.cleared} date${data.cleared === 1 ? "" : "s"} in ${month}` : `No dates to clear in ${month}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not clear the month"); }
    finally { setClearing(false); }
  };

  const markPosted = (post) => {
    if (!post.posted && !post.ready) { toast.error("Flip 'Ready to publish' on first"); return; }
    patchPost(post.id, { posted: !post.posted });
  };

  const viewPosts = campaignOnly ? posts.filter((p) => p.campaign_tag === "launch8w") : posts;
  const hasCampaign = posts.some((p) => p.campaign_tag === "launch8w");
  const postsOnDay = (dayStr) => viewPosts.filter((p) => p.scheduled_day === dayStr);

  const dropOnDay = (ds) => {
    const id = dragId;
    setDragId(null);
    setDragOverDay(null);
    if (!id) return;
    const post = posts.find((p) => p.id === id);
    if (!post || post.scheduled_day === ds) return;
    if (post.posted) { toast.error("Posted items can't be moved"); return; }
    patchPost(id, { scheduled_day: ds });
    toast.success(`Moved to ${ds}`);
  };

  const calendarCells = () => {
    const [y, m] = month.split("-").map(Number);
    const startDow = (new Date(y, m - 1, 1).getDay() + 6) % 7; // Monday = 0
    const days = new Date(y, m, 0).getDate();
    const cells = [];
    for (let i = 0; i < startDow; i++) cells.push(null);
    for (let d = 1; d <= days; d++) cells.push(d);
    while (cells.length % 7) cells.push(null);
    return cells;
  };

  const copyAll = async (id) => {    const e = edits[id] || {};
    const text = [`LinkedIn:\n${e.linkedin || ""}`, `X (Twitter):\n${e.x || ""}`, `Instagram:\n${e.instagram || ""}`].join("\n\n———\n\n");
    try { await navigator.clipboard.writeText(text); toast.success("All three versions copied"); }
    catch { toast.error("Could not copy"); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/learning/campaign/generate", { per_theme: 4, tone });
      const merged = [...posts, ...(data.posts || [])];
      setPosts(merged);
      const { e, p } = seedFrom(merged);
      setEdits(e); setActivePlat(p);
      toast.success(`Generated ${data.generated} posts`);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not generate posts"); }
    finally { setGenerating(false); }
  };

  const regenerate = async (id) => {
    setRegenId(id);
    try {
      const { data } = await api.post(`/admin/learning/campaign/posts/${id}/regenerate`, { tone });
      setPosts((list) => list.map((x) => (x.id === id ? data : x)));
      setEdits((s) => ({ ...s, [id]: { linkedin: combine(data.platforms.linkedin), x: combine(data.platforms.x), instagram: combine(data.platforms.instagram) } }));
    } catch (err) { toast.error(err.response?.data?.detail || "Could not regenerate"); }
    finally { setRegenId(null); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/learning/campaign/posts/${id}`);
      setPosts((list) => list.filter((x) => x.id !== id));
      toast.success("Post deleted");
    } catch { toast.error("Could not delete"); }
  };

  const copy = async (id, plat) => {
    const text = `${edits[id]?.[plat] || ""}\n\n${trackedLink(id, plat)}`;
    try { await navigator.clipboard.writeText(text); toast.success("Post + tracked link copied to clipboard"); }
    catch { toast.error("Could not copy"); }
  };

  const scheduledCount = posts.filter((p) => p.scheduled_day && !p.posted).length;
  const postedCount = posts.filter((p) => p.posted).length;
  const monthScheduled = posts.filter((p) => (p.scheduled_day || "").startsWith(month));
  const themeMonthCounts = Object.keys(THEME_LABELS).map((t) => ({ t, n: monthScheduled.filter((p) => p.theme === t).length }));

  return (
    <div data-testid="campaign-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h2 className="font-heading text-lg font-bold text-navy flex items-center gap-2"><Megaphone size={20} weight="fill" style={{ color: "#C2410C" }} /> Social campaign</h2>
          <p className="text-sm text-slate-500">Pre-made, on-brand posts to get WorkWithLottie known — pairs with your video shorts.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="campaign-tone">
            {TONES.map(([k, label]) => (
              <button key={k} onClick={() => setTone(k)} data-testid={`campaign-tone-${k}`}
                className={`text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors ${tone === k ? "text-white" : "text-slate-600 hover:text-navy"}`}
                style={tone === k ? { background: "#0E103F" } : {}}>{label}</button>
            ))}
          </div>
          <button onClick={generate} disabled={generating} data-testid="campaign-generate-btn"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white px-3 py-2 rounded-xl disabled:opacity-60" style={{ background: "#C2410C" }}>
            {generating ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />}
            {posts.length ? "Generate more" : "Generate posts"}
          </button>
          <button onClick={toggleAnalytics} data-testid="campaign-analytics-toggle"
            className={`inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl border transition-colors ${showAnalytics ? "border-[#0E103F] text-white" : "border-slate-200 text-navy hover:border-[#0E103F]"}`}
            style={showAnalytics ? { background: "#0E103F" } : {}}>
            <ChartLineUp size={15} weight="bold" /> Analytics
          </button>
          <button onClick={() => setShowSeedDialog(true)} disabled={seeding} data-testid="campaign-seed-launch-btn"
            title="Load the 8-week WorkWithLottie launch campaign (40 posts + branded images)"
            className="inline-flex items-center gap-2 text-sm font-semibold px-3 py-2 rounded-xl border border-slate-200 text-navy hover:border-[#C2410C] transition-colors disabled:opacity-60">
            {seeding ? <CircleNotch size={15} className="animate-spin" /> : <CalendarPlus size={15} weight="bold" style={{ color: "#C2410C" }} />} Load 8-week launch
          </button>
        </div>
      </div>

      {posts.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-5 text-xs" data-testid="campaign-month-summary">
          <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">{posts.length} posts</span>
          <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 font-semibold" data-testid="campaign-scheduled-count">{scheduledCount} scheduled</span>
          <span className="px-2.5 py-1 rounded-full bg-green-50 text-green-700 font-semibold" data-testid="campaign-posted-count">{postedCount} posted</span>
          <div className="flex flex-wrap items-center gap-2 ml-auto rounded-xl border border-slate-200 bg-white px-2 py-1.5" data-testid="campaign-autofill-bar">
            <span className="font-semibold text-slate-500 pl-1">Auto-fill</span>
            <input type="month" value={month} data-testid="campaign-autofill-month"
              onChange={(e) => setMonth(e.target.value)}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1 text-navy" />
            <label className="inline-flex items-center gap-1 text-slate-500 cursor-pointer select-none">
              <input type="checkbox" checked={overwrite} data-testid="campaign-autofill-overwrite"
                onChange={(e) => setOverwrite(e.target.checked)} className="accent-[#C2410C]" />
              Overwrite
            </label>
            <button onClick={autoFill} disabled={autoFilling} data-testid="campaign-autofill-btn"
              className="inline-flex items-center gap-1.5 font-semibold text-white rounded-lg px-3 py-1.5 disabled:opacity-60" style={{ background: "#0E103F" }}>
              {autoFilling ? <CircleNotch size={13} className="animate-spin" /> : <CalendarPlus size={13} weight="fill" />} Fill month
            </button>
            <button onClick={clearMonth} disabled={clearing} data-testid="campaign-clearmonth-btn"
              className="inline-flex items-center gap-1.5 font-semibold text-navy border border-slate-200 rounded-lg px-3 py-1.5 hover:border-red-300 hover:text-red-500 transition-colors disabled:opacity-60">
              {clearing ? <CircleNotch size={13} className="animate-spin" /> : <Eraser size={13} weight="bold" />} Clear
            </button>
            <button onClick={bulkCopyMonth} disabled={bulkCopy} data-testid="campaign-bulkcopy-btn"
              title="Let Lottie draft captions + hashtags for every scheduled post this month"
              className="inline-flex items-center gap-1.5 font-semibold text-navy border border-slate-200 rounded-lg px-3 py-1.5 hover:border-[#C2410C] transition-colors disabled:opacity-60">
              {bulkCopy ? <CircleNotch size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" style={{ color: "#C2410C" }} />} Draft copy
            </button>
            <button onClick={bulkImages} disabled={bulkImg} data-testid="campaign-bulkimg-btn"
              title="Generate an on-brand image for every scheduled post this month"
              className="inline-flex items-center gap-1.5 font-semibold text-navy border border-slate-200 rounded-lg px-3 py-1.5 hover:border-[#C2410C] transition-colors disabled:opacity-60">
              {bulkImg ? <CircleNotch size={13} className="animate-spin" /> : <ImageIcon size={13} weight="bold" style={{ color: "#C2410C" }} />} Images
            </button>
          </div>
          {hasCampaign && (
            <button onClick={() => setCampaignOnly((v) => !v)} data-testid="campaign-filter-launch"
              title="Show only the 8-week launch campaign posts"
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors ${campaignOnly ? "border-[#C2410C] text-white" : "border-slate-200 text-slate-600 hover:text-navy hover:border-[#C2410C]"}`}
              style={campaignOnly ? { background: "#C2410C" } : {}}>
              <Megaphone size={13} weight="bold" /> Launch only
            </button>
          )}
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="campaign-viewmode">
            {[["list", "List", ListBullets], ["calendar", "Calendar", CalendarBlank]].map(([k, label, Icon]) => (
              <button key={k} onClick={() => setViewMode(k)} data-testid={`campaign-view-${k}`}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md transition-colors ${viewMode === k ? "text-white" : "text-slate-600 hover:text-navy"}`}
                style={viewMode === k ? { background: "#0E103F" } : {}}>
                <Icon size={13} weight="bold" /> {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <button onClick={() => setShowViewPrefs((v) => !v)} data-testid="campaign-viewprefs-btn"
              title="Set the default view and how groups open"
              className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition-colors ${showViewPrefs ? "border-[#0E103F] text-navy" : "border-slate-200 text-slate-600 hover:text-navy hover:border-[#0E103F]"}`}>
              <Gear size={14} weight="bold" /> View
            </button>
            {showViewPrefs && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowViewPrefs(false)} data-testid="campaign-viewprefs-backdrop" />
                <div className="absolute right-0 mt-1 z-20 w-64 rounded-xl border border-slate-200 bg-white shadow-lg p-3" data-testid="campaign-viewprefs-panel">
                  <div className="text-[0.65rem] uppercase tracking-wide font-bold text-slate-400 mb-1.5">Default view when opening Social</div>
                  <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 w-full mb-3">
                    {[["list", "List"], ["calendar", "Calendar"]].map(([k, label]) => (
                      <button key={k} onClick={() => applyDefaultView(k)} data-testid={`campaign-defaultview-${k}`}
                        className={`flex-1 text-xs font-semibold px-2 py-1.5 rounded-md transition-colors ${defaultView === k ? "text-white" : "text-slate-600 hover:text-navy"}`}
                        style={defaultView === k ? { background: "#0E103F" } : {}}>
                        {label}{defaultView === k ? " ★" : ""}
                      </button>
                    ))}
                  </div>
                  <label className={`flex items-start gap-2 cursor-pointer select-none rounded-lg p-2 transition-colors ${defaultView === "list" ? "hover:bg-slate-50" : "opacity-40 cursor-not-allowed"}`}>
                    <input type="checkbox" checked={collapseDefault} disabled={defaultView !== "list"}
                      data-testid="campaign-collapse-default"
                      onChange={(e) => applyCollapseDefault(e.target.checked)} className="accent-[#0E103F] mt-0.5" />
                    <span className="text-xs text-navy leading-snug">Collapse post groups by type
                      <span className="block text-[0.65rem] text-slate-400">Start with Pain points, Benefits, etc. collapsed in List view.</span>
                    </span>
                  </label>
                </div>
              </>
            )}
          </div>
          <span className="basis-full text-slate-400">Auto-fill spreads a balanced mix of themes across the month's weekdays. Overwrite reshuffles already-scheduled (unposted) posts; Clear wipes the month's unposted dates.</span>
        </div>
      )}

      {hasCampaign && (
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 mb-5" data-testid="campaign-health-card">
          <div className="flex items-center justify-between mb-2.5">
            <span className="text-[0.65rem] uppercase tracking-wide font-bold text-slate-400 flex items-center gap-1.5">
              <Megaphone size={13} weight="fill" style={{ color: "#C2410C" }} /> Campaign health {campaignOnly ? "· launch campaign" : ""}
            </span>
          </div>
          {(() => {
            const src = campaignOnly ? viewPosts : posts;
            const ready = src.filter((p) => p.ready).length;
            const scheduled = src.filter((p) => p.scheduled_day).length;
            const posted = src.filter((p) => p.posted).length;
            const cells = [
              ["Total posts", src.length, "text-navy"],
              ["Ready", ready, "text-[#C2410C]"],
              ["Scheduled", scheduled, "text-blue-700"],
              ["Posted", posted, "text-green-700"],
              ["Trials driven", analytics ? analytics.total_attributed : "—", "text-green-700"],
            ];
            return (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                {cells.map(([label, val, cls]) => (
                  <div key={label} className="rounded-lg bg-slate-50/70 border border-slate-100 px-3 py-2.5 text-center" data-testid={`campaign-health-${String(label).toLowerCase().replace(/ /g, "-")}`}>
                    <div className={`text-xl font-bold ${cls}`}>{val}</div>
                    <div className="text-[0.62rem] uppercase tracking-wide font-semibold text-slate-400 mt-0.5">{label}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

      {posts.length > 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-3.5 mb-5" data-testid="campaign-theme-balance">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[0.65rem] uppercase tracking-wide font-bold text-slate-400">Theme balance · {monthLabel(month)}</span>
            <span className="text-[0.65rem] font-semibold text-slate-400">{monthScheduled.length} on the calendar</span>
          </div>
          {monthScheduled.length === 0 ? (
            <p className="text-xs text-slate-400">No posts scheduled in {monthLabel(month)} yet — hit "Fill month" to spread a balanced mix.</p>
          ) : (
            <>
              <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100">
                {themeMonthCounts.filter((x) => x.n > 0).map((x) => (
                  <div key={x.t} data-testid={`campaign-balance-seg-${x.t}`}
                    style={{ width: `${(x.n / monthScheduled.length) * 100}%`, background: THEME_DOT[x.t] }}
                    title={`${THEME_LABELS[x.t]}: ${x.n}`} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                {themeMonthCounts.map((x) => (
                  <span key={x.t} className={`inline-flex items-center gap-1.5 text-[0.68rem] ${x.n === 0 ? "text-slate-300" : "text-slate-600"}`} data-testid={`campaign-balance-count-${x.t}`}>
                    <span className="w-2 h-2 rounded-full" style={{ background: x.n === 0 ? "#cbd5e1" : THEME_DOT[x.t] }} />
                    {THEME_LABELS[x.t]} <span className="font-bold text-navy">{x.n}</span>
                  </span>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {showSeedDialog && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" data-testid="campaign-seed-dialog"
          onClick={() => !seeding && setShowSeedDialog(false)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-bold text-navy flex items-center gap-2">
              <CalendarPlus size={20} weight="bold" style={{ color: "#C2410C" }} /> Load 8-week launch campaign
            </h3>
            <p className="text-sm text-slate-500 mt-1">40 posts (Mon–Fri × 8 weeks) with branded images. This replaces any previously loaded launch posts.</p>
            <label className="block mt-4 text-xs font-bold uppercase tracking-wide text-slate-400">Campaign start (Week 1, Monday)</label>
            <input type="date" value={seedStart} onChange={(e) => setSeedStart(e.target.value)} data-testid="campaign-seed-start-input"
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-navy outline-none focus:border-[#C2410C]" />
            <p className="text-[0.7rem] text-slate-400 mt-1.5">Not a Monday? We'll snap it back to that week's Monday automatically.</p>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowSeedDialog(false)} disabled={seeding} data-testid="campaign-seed-cancel"
                className="text-sm font-semibold text-slate-500 px-4 py-2 rounded-xl hover:text-navy disabled:opacity-50">Cancel</button>
              <button onClick={seedLaunch} disabled={seeding || !seedStart} data-testid="campaign-seed-confirm"
                className="inline-flex items-center gap-2 text-sm font-semibold text-white px-4 py-2 rounded-xl disabled:opacity-60" style={{ background: "#C2410C" }}>
                {seeding ? <CircleNotch size={15} className="animate-spin" /> : <CalendarPlus size={15} weight="bold" />} Load campaign
              </button>
            </div>
          </div>
        </div>
      )}

      {showAnalytics && (
        <div className="rounded-xl border border-slate-200 bg-white p-4 mb-5" data-testid="campaign-analytics-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="font-heading text-sm font-bold text-navy flex items-center gap-2"><ChartLineUp size={16} weight="bold" style={{ color: "#0E103F" }} /> Post analytics</h3>
              <p className="text-xs text-slate-500">Trial sign-ups attributed to each post's tracked link. Use "Copy link" or Publish to share the tracked link.</p>
            </div>
            <button onClick={loadAnalytics} disabled={loadingAnalytics} data-testid="campaign-analytics-refresh"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-slate-200 rounded-lg px-2.5 py-1.5 hover:border-[#C2410C] transition-colors disabled:opacity-50">
              {loadingAnalytics ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />} Refresh
            </button>
          </div>
          {loadingAnalytics && !analytics ? (
            <div className="text-slate-500 text-sm flex items-center gap-2 py-6"><CircleNotch size={15} className="animate-spin" /> Loading analytics…</div>
          ) : analytics ? (
            <>
              <div className="flex flex-wrap gap-2 mb-3 text-xs" data-testid="campaign-analytics-summary">
                <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold">{analytics.total_visits || 0} link visits</span>
                <span className="px-2.5 py-1 rounded-full bg-green-50 text-green-700 font-semibold">{analytics.total_attributed} sign-ups from posts</span>
                <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">
                  {analytics.total_visits ? Math.round((analytics.total_attributed / analytics.total_visits) * 100) : 0}% visit → trial
                </span>
                <span className="px-2.5 py-1 rounded-full bg-slate-100 text-slate-600 font-semibold">{analytics.total_signups} total trial sign-ups</span>
              </div>
              {analytics.posts?.filter((p) => p.signups > 0 || p.visits > 0).length === 0 ? (
                <p className="text-xs text-slate-400 py-2" data-testid="campaign-analytics-empty">No tracked activity yet. Share a post's tracked link — visits and trials started from it appear here.</p>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-3 px-3 pb-1 text-[0.6rem] uppercase tracking-wide font-bold text-slate-400">
                    <span className="w-2 shrink-0" />
                    <span className="flex-1">Post</span>
                    <span className="w-14 text-right shrink-0">Visits</span>
                    <span className="w-14 text-right shrink-0">Trials</span>
                    <span className="w-12 text-right shrink-0">Conv.</span>
                  </div>
                  {analytics.posts.filter((p) => p.signups > 0 || p.visits > 0).slice(0, 12).map((p) => (
                    <div key={p.id} className="flex items-center gap-3 rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2" data-testid={`campaign-analytics-row-${p.id}`}>
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: THEME_DOT[p.theme] || "#94a3b8" }} />
                      <span className="text-xs text-navy flex-1 line-clamp-1" title={p.headline}>{p.headline}</span>
                      <span className="w-14 text-right text-sm font-semibold text-blue-700 shrink-0" data-testid={`campaign-analytics-visits-${p.id}`}>{p.visits || 0}</span>
                      <span className="w-14 text-right text-sm font-bold text-green-700 shrink-0" data-testid={`campaign-analytics-signups-${p.id}`}>{p.signups}</span>
                      <span className="w-12 text-right text-xs font-semibold text-slate-500 shrink-0" data-testid={`campaign-analytics-conv-${p.id}`}>{p.conversion || 0}%</span>
                    </div>
                  ))}
                </div>
              )}
              {analytics.channels?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-slate-100" data-testid="campaign-analytics-channels">
                  <div className="text-[0.6rem] uppercase tracking-wide font-bold text-slate-400 mb-2">Which platform converts best</div>
                  <div className="flex flex-wrap gap-2">
                    {analytics.channels.map((c) => (
                      <div key={c.channel} className="rounded-lg border border-slate-100 bg-slate-50/60 px-3 py-2 min-w-[120px]" data-testid={`campaign-channel-${c.channel}`}>
                        <div className="text-xs font-bold text-navy">{c.label}</div>
                        <div className="text-[0.65rem] text-slate-500 mt-0.5">{c.visits} visits · <span className="text-green-700 font-semibold">{c.signups} trials</span></div>
                        <div className="text-[0.65rem] font-semibold text-[#C2410C]">{c.conversion}% conversion</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}


      {loading ? (
        <div className="text-slate-500 text-sm flex items-center gap-2 py-10"><CircleNotch size={16} className="animate-spin" /> Loading campaign…</div>
      ) : posts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="campaign-empty">
          <Megaphone size={32} weight="fill" className="mx-auto mb-3" style={{ color: "#C2410C" }} />
          <p className="font-heading font-bold text-navy mb-1">No posts yet</p>
          <p className="text-sm text-slate-500 mb-4">Generate a batch of ready-to-post, on-brand social content across five themes.</p>
          <button onClick={generate} disabled={generating} data-testid="campaign-generate-empty"
            className="inline-flex items-center gap-2 text-sm font-semibold text-white px-4 py-2 rounded-xl disabled:opacity-60" style={{ background: "#C2410C" }}>
            {generating ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} weight="fill" />} Generate posts
          </button>
        </div>
      ) : viewMode === "calendar" ? (
        <div data-testid="campaign-calendar" className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-center gap-4 mb-3">
            <button onClick={() => shiftMonth(-1)} data-testid="campaign-month-prev"
              className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-navy hover:border-[#C2410C] hover:text-[#C2410C] transition-colors"><CaretLeft size={16} weight="bold" /></button>
            <span className="font-heading font-bold text-navy text-base min-w-[150px] text-center" data-testid="campaign-month-label">{monthLabel(month)}</span>
            <button onClick={() => shiftMonth(1)} data-testid="campaign-month-next"
              className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-slate-200 text-navy hover:border-[#C2410C] hover:text-[#C2410C] transition-colors"><CaretRight size={16} weight="bold" /></button>
          </div>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAY_HDRS.map((d) => (
              <div key={d} className="text-[0.65rem] uppercase font-bold text-slate-400 text-center py-1">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {calendarCells().map((d, i) => {
              if (!d) return <div key={i} className="min-h-[86px] rounded-lg bg-slate-50/40" />;
              const ds = `${month}-${String(d).padStart(2, "0")}`;
              const dayPosts = postsOnDay(ds);
              const wd = new Date(`${ds}T00:00:00`).getDay();
              const weekend = wd === 0 || wd === 6;
              return (
                <div key={i} data-testid={`campaign-cal-day-${d}`}
                  onClick={() => setPickerDay(ds)}
                  onDragOver={(e) => { e.preventDefault(); setDragOverDay(ds); }}
                  onDragLeave={() => setDragOverDay((cur) => (cur === ds ? null : cur))}
                  onDrop={() => dropOnDay(ds)}
                  className={`group min-h-[86px] rounded-lg border p-1.5 transition-colors cursor-pointer ${dragOverDay === ds ? "border-[#C2410C] bg-orange-50 ring-2 ring-orange-200" : weekend ? "border-slate-100 bg-slate-50/50 hover:border-slate-300" : "border-slate-200 bg-white hover:border-[#C2410C]/50"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[0.7rem] font-bold text-slate-400">{d}</span>
                    <Plus size={11} weight="bold" className="text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="space-y-1">
                    {dayPosts.slice(0, 4).map((p) => (
                      <div key={p.id} title={p.posted ? THEME_LABELS[p.theme] || p.theme : `${THEME_LABELS[p.theme] || p.theme} — click to open · drag to move`} data-testid={`campaign-cal-chip-${p.id}`}
                        draggable={!p.posted}
                        onClick={(e) => { e.stopPropagation(); jumpToPost(p.id); }}
                        onDragStart={() => setDragId(p.id)}
                        onDragEnd={() => { setDragId(null); setDragOverDay(null); }}
                        className={`flex items-center gap-1 text-[0.62rem] font-semibold rounded px-1 py-0.5 border-l-[3px] cursor-pointer hover:brightness-95 ${p.posted ? "line-through text-slate-400" : "text-navy active:cursor-grabbing"} ${dragId === p.id ? "opacity-40" : ""}`}
                        style={{ background: `${THEME_DOT[p.theme] || "#94a3b8"}1a`, borderLeftColor: THEME_DOT[p.theme] || "#94a3b8" }}>
                        {p.image_path && <img src={mediaUrl(p)} alt="" className="w-3.5 h-3.5 rounded-sm object-cover shrink-0" />}
                        <span className="truncate">{THEME_LABELS[p.theme] || p.theme}</span>
                        {p.posted ? <CheckCircle size={11} weight="fill" className="ml-auto text-green-600 shrink-0" />
                          : p.ready ? <ShieldCheck size={11} weight="fill" className="ml-auto shrink-0" style={{ color: "#C2410C" }} /> : null}
                      </div>
                    ))}
                    {dayPosts.length > 4 && <div className="text-[0.6rem] text-slate-400 pl-1">+{dayPosts.length - 4} more</div>}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4 pt-3 border-t border-slate-100 text-[0.65rem] text-slate-500">
            {Object.entries(THEME_LABELS).map(([k, label]) => (
              <span key={k} className="inline-flex items-center gap-1">
                <span className="w-2 h-2 rounded-full" style={{ background: THEME_DOT[k] }} /> {label}
              </span>
            ))}
            <span className="inline-flex items-center gap-1 ml-2"><ShieldCheck size={12} weight="fill" style={{ color: "#C2410C" }} /> Ready</span>
            <span className="inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" className="text-green-600" /> Posted</span>
            <span className="ml-auto text-slate-400">Tip: click a day to add a post, a chip to open it, or drag a chip to reschedule.</span>
          </div>
        </div>
      ) : (
        themes.filter((t) => viewPosts.some((p) => p.theme === t))
          .sort((a, b) => SECTION_ORDER.indexOf(a) - SECTION_ORDER.indexOf(b))
          .map((theme) => {
          const themePosts = viewPosts.filter((p) => p.theme === theme);
          const isOpen = theme in collapsed ? !collapsed[theme] : !collapseDefault;
          return (
          <div key={theme} className="mb-6" data-testid={`campaign-theme-${theme}`}>
            <button onClick={() => setCollapsed((s) => ({ ...s, [theme]: !s[theme] }))} data-testid={`campaign-theme-toggle-${theme}`}
              className="w-full flex items-center gap-2 mb-3 group">
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: THEME_DOT[theme] || "#94a3b8" }} />
              <h3 className="text-xs uppercase tracking-wide font-bold text-slate-500 group-hover:text-navy transition-colors">{THEME_LABELS[theme] || theme}</h3>
              <span className="text-[0.65rem] font-semibold text-slate-400">{themePosts.length}</span>
              {isOpen ? <CaretUp size={13} className="text-slate-400 ml-1" /> : <CaretDown size={13} className="text-slate-400 ml-1" />}
              <span className="flex-1 border-t border-slate-100 ml-2" />
            </button>
            {isOpen && (
            <div className="grid gap-4 md:grid-cols-2">
              {themePosts.map((post) => {
                const plat = activePlat[post.id] || "linkedin";
                const suggestion = suggestFor(post.theme);
                return (
                  <div key={post.id} className={`rounded-xl border border-l-4 p-4 transition-opacity ${post.posted ? "border-green-200 bg-green-50/40 opacity-70" : "border-slate-200 bg-white"}`}
                    style={{ borderLeftColor: post.posted ? undefined : (THEME_DOT[post.theme] || "#94a3b8") }} data-testid={`campaign-post-${post.id}`}>
                    {(post.title || post.week || post.post_time) && (
                      <div className="mb-2 pb-2 border-b border-slate-100" data-testid={`campaign-post-header-${post.id}`}>
                        {post.title && <h4 className="text-sm font-bold text-navy leading-snug">{post.title}</h4>}
                        <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-[0.62rem]">
                          {post.week && <span className="font-bold px-1.5 py-0.5 rounded bg-navy/5 text-navy" title={post.week_title || ""}>Week {post.week}</span>}
                          {post.scheduled_day && <span className="text-slate-500">{new Date(post.scheduled_day + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}</span>}
                          {post.post_time && <span className="inline-flex items-center gap-0.5 text-slate-500"><Clock size={10} weight="bold" style={{ color: "#C2410C" }} /> {post.post_time}</span>}
                          {(post.platforms_target || []).map((pl) => (
                            <span key={pl} className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold" data-testid={`campaign-platformtag-${post.id}-${pl.toLowerCase()}`}>{pl}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-2">
                      <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
                        {PLATFORMS.map(([k, label]) => (
                          <button key={k} onClick={() => setActivePlat((s) => ({ ...s, [post.id]: k }))} data-testid={`campaign-plat-${k}-${post.id}`}
                            className={`text-[0.7rem] font-semibold px-2.5 py-1 rounded-md transition-colors ${plat === k ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>{label}</button>
                        ))}
                      </div>
                      {post.posted && <span className="text-[0.65rem] font-bold text-green-700 inline-flex items-center gap-1"><CheckCircle size={13} weight="fill" /> Posted</span>}
                    </div>
                    <textarea rows={7} value={edits[post.id]?.[plat] || ""} data-testid={`campaign-textarea-${post.id}`}
                      onChange={(e) => setEdits((s) => ({ ...s, [post.id]: { ...s[post.id], [plat]: e.target.value } }))}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 text-navy text-xs leading-relaxed p-2.5 focus:outline-none focus:ring-2 focus:ring-orange-300" />
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <button onClick={() => copy(post.id, plat)} data-testid={`campaign-copy-${post.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-1.5 transition-transform hover:scale-[1.02]" style={{ background: "#C2410C" }}>
                        <Copy size={13} weight="bold" /> Copy
                      </button>
                      <button onClick={() => copyAll(post.id)} data-testid={`campaign-copyall-${post.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-slate-200 rounded-lg px-3 py-1.5 hover:border-[#C2410C] transition-colors">
                        <CopySimple size={13} weight="bold" /> Copy all
                      </button>
                      <span className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1" data-testid={`campaign-copylink-${post.id}`}>
                        <LinkSimple size={13} weight="bold" className="text-slate-500" />
                        <span className="text-[0.68rem] font-semibold text-slate-500">Link:</span>
                        {LINK_CHANNELS.map(([ch, label]) => (
                          <button key={ch} onClick={() => copyTracked(post.id, ch)} data-testid={`campaign-copylink-${ch}-${post.id}`}
                            title={`Copy tracked ${label} link (channel-tagged for analytics)`}
                            className="text-[0.68rem] font-semibold text-navy px-1.5 py-0.5 rounded hover:bg-slate-100 hover:text-[#C2410C] transition-colors">
                            {label}
                          </button>
                        ))}
                      </span>
                      <button onClick={() => plat === "linkedin" ? setLinkedinShare(post) : publishAssist(post, plat)} data-testid={`campaign-publish-${post.id}`}
                        title={plat === "linkedin" ? "Copy caption, link & image for LinkedIn" : `Copy caption, download any image, and open ${plat === "x" ? "X" : "Instagram"} to post`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-1.5 transition-transform hover:scale-[1.02]" style={{ background: "#0E103F" }}>
                        <PaperPlaneTilt size={13} weight="fill" /> Publish to {PLATFORMS.find(([k]) => k === plat)?.[1].replace(" (Twitter)", "")}
                      </button>
                      <button onClick={() => regenerate(post.id)} disabled={regenId === post.id} data-testid={`campaign-regen-${post.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-slate-200 rounded-lg px-3 py-1.5 hover:border-[#C2410C] transition-colors disabled:opacity-50">
                        {regenId === post.id ? <CircleNotch size={13} className="animate-spin" /> : <ArrowsClockwise size={13} weight="bold" />} Regenerate
                      </button>
                      <button onClick={() => remove(post.id)} data-testid={`campaign-delete-${post.id}`}
                        className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-red-500 transition-colors">
                        <Trash size={14} />
                      </button>
                    </div>
                    <div className="flex items-start gap-3 mt-2 pt-2 border-t border-slate-100" data-testid={`campaign-media-${post.id}`}>
                      {post.image_path ? (
                        <img src={mediaUrl(post)} alt="Post image" data-testid={`campaign-image-${post.id}`}
                          className="w-16 h-16 rounded-lg object-cover border border-slate-200 shrink-0" />
                      ) : (
                        <div className="w-16 h-16 rounded-lg border border-dashed border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
                          <ImageIcon size={20} className="text-slate-300" />
                        </div>
                      )}
                      {post.story_path && (
                        <a href={`${API}/learning/campaign/media/${post.id}?variant=story`} target="_blank" rel="noopener noreferrer"
                          title="9:16 Story version — click to open full size" data-testid={`campaign-storythumb-${post.id}`} className="shrink-0 relative">
                          <img src={`${API}/learning/campaign/media/${post.id}?variant=story&v=${encodeURIComponent(post.image_updated || "")}`} alt="Story 9:16"
                            className="w-9 h-16 rounded-lg object-cover border border-slate-200" />
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[0.5rem] font-bold text-white bg-navy/80 px-1 rounded">9:16</span>
                        </a>
                      )}
                      <div className="flex flex-wrap items-center gap-2 flex-1">
                        <button onClick={() => genImage(post.id, post.image_aspect === "9:16" ? "9:16" : "1:1", post.image_bg || "lottie")} disabled={imgBusy === post.id} data-testid={`campaign-genimg-${post.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-slate-200 rounded-lg px-2.5 py-1.5 hover:border-[#C2410C] transition-colors disabled:opacity-50">
                          {imgBusy === post.id ? <CircleNotch size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" style={{ color: "#C2410C" }} />}
                          {post.image_source === "generated" ? "Regenerate image" : "Generate image"}
                        </button>
                        <label data-testid={`campaign-uploadimg-${post.id}`}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-slate-200 rounded-lg px-2.5 py-1.5 hover:border-[#C2410C] transition-colors cursor-pointer">
                          <UploadSimple size={13} weight="bold" /> Upload
                          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                            onChange={(e) => { uploadImage(post.id, e.target.files?.[0]); e.target.value = ""; }} />
                        </label>
                        {post.image_path && (
                          <button onClick={() => removeImage(post.id)} data-testid={`campaign-rmimg-${post.id}`}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-400 hover:text-red-500 transition-colors">
                            <Trash size={13} /> Remove
                          </button>
                        )}
                        {post.story_path && (
                          <a href={`${API}/learning/campaign/media/${post.id}?variant=story`} target="_blank" rel="noopener noreferrer"
                            download={`lottie-story-${post.id}.png`} data-testid={`campaign-story-${post.id}`}
                            title="Download the 9:16 Story version for Instagram/Facebook Stories"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-slate-200 rounded-lg px-2.5 py-1.5 hover:border-[#C2410C] transition-colors">
                            <DownloadSimple size={13} weight="bold" /> Story 9:16
                          </a>
                        )}
                        <div className="basis-full flex items-center gap-1.5 mt-1">
                          <span className="text-[0.6rem] uppercase font-bold text-slate-400 mr-0.5">Background</span>
                          {BG_STYLES.map((b) => (
                            <button key={b.key} onClick={() => genImage(post.id, post.image_aspect === "9:16" ? "9:16" : "1:1", b.key)}
                              disabled={imgBusy === post.id} title={b.label} data-testid={`campaign-bg-${b.key}-${post.id}`}
                              className={`w-6 h-6 rounded-md border transition-all disabled:opacity-50 ${post.image_bg === b.key && post.image_source === "generated" ? "ring-2 ring-[#C2410C] ring-offset-1 border-transparent" : "border-slate-200 hover:scale-110"}`}
                              style={b.css} />
                          ))}
                        </div>
                        <span className="basis-full text-[0.65rem] text-slate-400">A branded image or a paired short — either works as your post visual. Pick a background to generate.</span>
                        {post.image_brief && (
                          <span className="basis-full text-[0.65rem] text-slate-400 italic border-l-2 border-slate-200 pl-2 mt-0.5" data-testid={`campaign-imagebrief-${post.id}`}>
                            <b className="not-italic text-slate-500">Image brief:</b> {post.image_brief}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-slate-100">
                      <input type="date" value={post.scheduled_day || ""} data-testid={`campaign-date-${post.id}`}
                        onChange={(e) => patchPost(post.id, { scheduled_day: e.target.value })}
                        className="text-xs border border-slate-200 rounded-lg px-2 py-1 text-navy" />
                      <span className="inline-flex items-center gap-1 text-[0.7rem] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1" data-testid={`campaign-besttime-${post.id}`} title={`Suggested posting window for ${plat}`}>
                        <Clock size={12} weight="bold" style={{ color: "#C2410C" }} /> {BEST_TIMES[plat]}
                      </span>
                      <button onClick={() => patchPost(post.id, { ready: !post.ready })} data-testid={`campaign-ready-${post.id}`}
                        className={`inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1.5 border transition-colors ${post.ready ? "border-orange-300 text-[#C2410C] bg-orange-50" : "border-slate-200 text-slate-500 hover:text-navy"}`}>
                        <ShieldCheck size={14} weight={post.ready ? "fill" : "regular"} /> {post.ready ? "Ready" : "Mark ready"}
                      </button>
                      <button onClick={() => markPosted(post)} data-testid={`campaign-posted-${post.id}`}
                        disabled={!post.ready && !post.posted} title={!post.ready && !post.posted ? "Flip 'Ready to publish' on first" : ""}
                        className={`inline-flex items-center gap-1 text-xs font-semibold rounded-lg px-2.5 py-1.5 border transition-colors ${post.posted ? "border-green-300 text-green-700 bg-green-50" : post.ready ? "border-slate-200 text-slate-500 hover:text-navy" : "border-slate-100 text-slate-300 cursor-not-allowed"}`}>
                        <CheckCircle size={14} weight={post.posted ? "fill" : "regular"} /> {post.posted ? "Posted" : "Mark posted"}
                      </button>
                      <div className="flex items-center gap-1 ml-auto">
                        <FilmSlate size={14} className="text-slate-400" />
                        <select value={post.paired_slug || ""} data-testid={`campaign-pair-${post.id}`}
                          onChange={(e) => patchPost(post.id, { paired_slug: e.target.value })}
                          className="text-xs border border-slate-200 rounded-lg px-2 py-1 text-navy max-w-[150px]">
                          <option value="">Pair a short…</option>
                          {walkthroughs.map((w) => (
                            <option key={w.slug} value={w.slug}>{w.slug === suggestion ? "✨ " : ""}{w.title}</option>
                          ))}
                        </select>
                        <button onClick={() => downloadShort(post.id, post.paired_slug || suggestion)} disabled={clipDl === post.id} data-testid={`campaign-clip-${post.id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-navy border border-slate-200 rounded-lg px-2.5 py-1.5 hover:border-[#C2410C] transition-colors disabled:opacity-50">
                          {clipDl === post.id ? <CircleNotch size={13} className="animate-spin" /> : <DownloadSimple size={13} weight="bold" />} Short
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            )}
          </div>
          );
        })
      )}

      <Dialog open={!!pickerDay} onOpenChange={(o) => !o && setPickerDay(null)}>
        <DialogContent className="max-w-md" data-testid="campaign-day-picker">
          <DialogHeader>
            <DialogTitle className="font-heading text-navy">
              Schedule a post on {pickerDay ? monthLabel(pickerDay.slice(0, 7)).replace(/\s\d{4}/, "") + " " + Number(pickerDay.slice(8)) : ""}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const onDay = pickerDay ? postsOnDay(pickerDay) : [];
            return (
              <div className="space-y-3">
                {onDay.length > 0 && (
                  <div>
                    <div className="text-[0.65rem] uppercase font-bold text-slate-400 mb-1.5">Already on this day</div>
                    <div className="space-y-1.5">
                      {onDay.map((p) => (
                        <div key={p.id} className="flex items-center gap-2 text-xs rounded-lg border border-slate-200 px-2.5 py-1.5">
                          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: THEME_DOT[p.theme] }} />
                          <span className="text-navy font-medium">{THEME_LABELS[p.theme]}</span>
                          {!p.posted && (
                            <button onClick={() => { patchPost(p.id, { scheduled_day: "" }); }} data-testid={`campaign-picker-remove-${p.id}`}
                              className="ml-auto text-slate-400 hover:text-red-500 transition-colors" title="Remove from this day"><Trash size={14} /></button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[0.65rem] uppercase font-bold text-slate-400">Add an unscheduled post</div>
                    <div className="inline-flex rounded-lg bg-slate-100 p-0.5" data-testid="campaign-picker-plat">
                      {PLATFORMS.map(([k, label]) => (
                        <button key={k} onClick={() => setPickerPlat(k)} data-testid={`campaign-picker-plat-${k}`}
                          className={`text-[0.62rem] font-semibold px-2 py-0.5 rounded-md transition-colors ${pickerPlat === k ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-navy"}`}>
                          {label === "X (Twitter)" ? "X" : label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {unscheduledPosts.length === 0 ? (
                    <p className="text-sm text-slate-500 py-2">Every post already has a date. Clear one, or generate more posts.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                      {unscheduledPosts.map((p) => {
                        const v = p.platforms?.[pickerPlat];
                        const preview = v?.text ? [v.text, (v.hashtags || []).map((h) => `#${h}`).join(" ")].filter(Boolean).join(" ") : "(no " + pickerPlat + " variant)";
                        return (
                        <button key={p.id} onClick={() => assignToDay(p.id)} data-testid={`campaign-picker-add-${p.id}`}
                          className="w-full text-left flex items-start gap-2 text-xs rounded-lg border border-slate-200 px-2.5 py-2 hover:border-[#C2410C] hover:bg-orange-50/40 transition-colors">
                          <span className="w-2 h-2 rounded-full shrink-0 mt-1" style={{ background: THEME_DOT[p.theme] }} />
                          <span className="flex-1 min-w-0">
                            <span className="block text-slate-500 font-semibold uppercase text-[0.6rem] tracking-wide">{THEME_LABELS[p.theme]}</span>
                            <span className={`block leading-snug ${v?.text ? "text-navy" : "text-slate-400 italic"}`} style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                              {preview}
                            </span>
                          </span>
                          <Plus size={14} weight="bold" className="text-[#C2410C] shrink-0 mt-0.5" />
                        </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* LinkedIn guided share — copy caption+link, copy/download image, open composer */}
      <Dialog open={!!linkedinShare} onOpenChange={(o) => !o && setLinkedinShare(null)}>
        <DialogContent className="max-w-lg rounded-lg border-slate-200 max-h-[92vh] overflow-y-auto" data-testid="linkedin-share-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl text-navy">Post to LinkedIn</DialogTitle>
          </DialogHeader>
          {linkedinShare && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500 leading-relaxed">
                LinkedIn doesn't let apps pre-fill posts, so it's a quick 3 steps: copy the caption, copy the image, then paste both into LinkedIn.
              </p>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[0.7rem] font-bold uppercase tracking-wide text-slate-500">1 · Caption + tracked link</span>
                  <button onClick={() => copyLinkedinText(linkedinShare)} data-testid="linkedin-copy-text"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-1.5" style={{ background: "#C2410C" }}>
                    <Copy size={13} weight="bold" /> Copy text
                  </button>
                </div>
                <textarea readOnly rows={6} value={linkedinCaption(linkedinShare)} data-testid="linkedin-caption-text"
                  className="w-full rounded-lg border border-slate-200 bg-slate-50 text-navy text-xs leading-relaxed p-2.5 focus:outline-none focus:ring-2 focus:ring-orange-300" />
              </div>

              {linkedinShare.image_path ? (
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[0.7rem] font-bold uppercase tracking-wide text-slate-500">2 · Image</span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => copyImageToClipboard(linkedinShare)} disabled={imgCopyBusy} data-testid="linkedin-copy-image"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-3 py-1.5 disabled:opacity-60" style={{ background: "#0E103F" }}>
                        {imgCopyBusy ? <CircleNotch size={13} className="animate-spin" /> : <CopySimple size={13} weight="bold" />} Copy image
                      </button>
                      <button onClick={() => downloadImage(linkedinShare)} data-testid="linkedin-download-image"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy border border-slate-200 rounded-lg px-3 py-1.5 hover:border-[#C2410C] transition-colors">
                        <DownloadSimple size={13} weight="bold" /> Download
                      </button>
                    </div>
                  </div>
                  <img src={mediaUrl(linkedinShare)} alt="Post image" data-testid="linkedin-image-preview"
                    className="w-full max-h-56 rounded-lg object-contain border border-slate-200 bg-slate-50" />
                  <p className="text-[0.68rem] text-slate-400 mt-1">Tip: after pasting the caption, paste the image (⌘/Ctrl+V) directly into the LinkedIn post box, or attach the downloaded file.</p>
                </div>
              ) : (
                <div className="text-xs text-slate-400">No image on this post.</div>
              )}

              <div className="flex justify-between items-center pt-1 border-t border-slate-100">
                <span className="text-[0.7rem] font-bold uppercase tracking-wide text-slate-500">3 · Open LinkedIn</span>
                <a href="https://www.linkedin.com/feed/?shareActive=true" target="_blank" rel="noopener noreferrer"
                  data-testid="linkedin-open" onClick={() => { if (linkedinShare) setTimeout(() => setLinkedinShare(null), 300); }}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-white rounded-lg px-4 py-2 transition-transform hover:scale-[1.02]" style={{ background: "#0A66C2" }}>
                  <PaperPlaneTilt size={13} weight="fill" /> Open LinkedIn composer
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
